import 'amazon-connect-streams';
import { useLayoutEffect, useRef, Dispatch, SetStateAction } from 'react';
import { useAppContext } from '../../../AppContext';
import { BackdropState } from '../../../models/CatoContainerTypes';
import { restoreNoiseSuppression } from '../../../utils/voiceEnhancementStorage';
import chatRingtoneSrc from '../../../assets/chatRingtone.mp3';

interface UseConnectInitializationProps {
  containerRef: React.RefObject<HTMLDivElement>;
  instanceURL: string;
  samURL: string;
  region: string;
  setBackdropState: Dispatch<SetStateAction<BackdropState>>;
}

export function useConnectInitialization({
  containerRef,
  instanceURL,
  samURL,
  region,
  setBackdropState
}: UseConnectInitializationProps): void {
  const { setAgent, setAgentState } = useAppContext();
  const isInitializedRef = useRef(false);

  useLayoutEffect(() => {
    if (isInitializedRef.current) return;

    if (!containerRef.current) {
      console.warn('Container ref is not set.');
      return;
    }

    isInitializedRef.current = true;

    connect.core.initCCP(containerRef.current, {
      ccpUrl: `${instanceURL}/connect/ccp-v2/`,
      loginPopup: true,
      loginOptions: {
        autoClose: true
      },
      loginUrl: samURL,
      ccpLoadTimeout: 3000,
      region: region,
      softphone: {
        allowFramedSoftphone: true
      },
      chat: {
        ringtoneUrl: chatRingtoneSrc
      },
      pageOptions: {
        enableAudioDeviceSettings: true
      }
    });

    connect.core.onIframeRetriesExhausted(() => {
      setBackdropState(BackdropState.AuthenticationExpired);
    });

    connect.core.onInitialized(() => {
      setBackdropState(BackdropState.None);
    });

    connect.core.onAuthFail(() => {
      setBackdropState(BackdropState.AuthenticationExpired);
    });

    connect.core.onAccessDenied(() => {
      setBackdropState(BackdropState.AuthenticationExpired);
    });

    connect.agent(agent => {
      setAgent(agent);
      restoreNoiseSuppression(agent);

      agent.onRefresh(() => {
        setAgentState(agent.getState());
      });
    });
  }, [containerRef, instanceURL, region, samURL, setAgent, setAgentState, setBackdropState]);
}
